<script setup lang="ts">
import { computed, withDefaults, ref } from 'vue';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import AreaVideos from '@/components/videos/AreaVideos.vue';
import { Video } from '@ilteducation/videos';
import useGuiStore from '@/store/gui';
import useAreasWithVideos from '@/composables/use-areas-with-videos';

const props = withDefaults(
  defineProps<{
    subject: Category;
    subjectVideos: Video[];
    isSearchResults: boolean;
  }>(),
  {
    subject: () => ({}) as Category,
    subjectVideos: () => [],
    isSearchResults: false,
  },
);

const slugToId = (anchor: string) => `area_anchor_${anchor}`;

const scrollToAnchor = (anchor: string) => {
  const elem = document.getElementById(slugToId(anchor));
  if (!elem) return;
  elem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  requestAnimationFrame(() => elem.focus?.({ preventScroll: true }));
};

const { getThemeName } = useGuiStore();
const videoAreaIds = computed(() =>
  props.subjectVideos.flatMap((video) => video.orderedAreas.flatMap((area) => area.id)),
);
const searchFilter = (area: Area) => !props.isSearchResults || videoAreaIds.value.includes(area.id);
const { areasWithVideos } = useAreasWithVideos();
const areas = computed(() =>
  areasWithVideos.value.filter((area) => area.categoryId === props.subject.id && area.active).filter(searchFilter),
);

type AreaVideosExpose = { isReady: boolean };
const areaRefs = ref<(AreaVideosExpose | null)[]>([]);

const ready = computed(() => areaRefs.value.length > 0 && areaRefs.value.every((c) => c?.isReady));

defineExpose({
  scrollToAnchor,
  ready,
});
</script>

<template>
  <v-sheet class="bg-transparent" :theme="getThemeName(subject?.slug)">
    <v-container class="custom-container mb-5">
      <v-row v-for="subjectArea in areas" :key="subjectArea.id" class="subject-row">
        <v-col class="mx-0 px-0">
          <h2 :id="slugToId(subjectArea.slug)" class="w-100 heading bg-subject mb-6 mt-0 px-4 py-3" tabindex="-1">
            {{ subjectArea.name }}
          </h2>
          <AreaVideos
            ref="areaRefs"
            :ref_for="true"
            :videos="subjectVideos"
            :area="subjectArea"
            :subject-id="subject.id"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<style scoped lang="scss">
.custom-container {
  max-width: 90rem !important;
}

.heading {
  font-size: 1.125rem;
  scroll-margin-top: 90px;
}

.subject-row {
  .area-description {
    line-height: 1.25;
  }
}
</style>
